import getOrCreateCanvas from '../../helpers/getOrCreateCanvas';
import GenericViewport from '../GenericViewport';
import { ViewportType } from '../../../enums';
import { getDefaultECGValueRange } from '../../../utilities/ECGUtilities';
import { DefaultECGDataProvider } from './DefaultECGDataProvider';
import { createECGRenderPathResolver } from './ECGRenderPathResolver';
import { createDefaultECGViewState, normalizeECGViewState, } from './ecgViewportCamera';
import ECGResolvedView from './ECGResolvedView';
const ECG_AMPLITUDE_INDEX_SIZE = 65536;
class ECGViewport extends GenericViewport {
    static get useCustomRenderingPipeline() {
        return true;
    }
    getUseCustomRenderingPipeline() {
        return true;
    }
    constructor(args) {
        super(args);
        this.type = ViewportType.ECG_NEXT;
        this.renderingEngineId = args.renderingEngineId;
        this.canvas = getOrCreateCanvas(this.element);
        this.canvasContext = this.canvas.getContext('2d');
        this.dataProvider = args.dataProvider || new DefaultECGDataProvider();
        this.renderPathResolver =
            args.renderPathResolver || createECGRenderPathResolver();
        this.renderContext = {
            viewportId: this.id,
            type: 'ecg',
            renderingEngineId: this.renderingEngineId,
            element: this.element,
            canvas: this.canvas,
            canvasContext: this.canvasContext,
        };
        this.viewState = createDefaultECGViewState({
            timeRange: [0, 1],
            valueRange: [-1, 1],
        });
        this.element.setAttribute('data-viewport-uid', this.id);
        this.element.setAttribute('data-rendering-engine-uid', this.renderingEngineId);
        this.resize();
    }
    async setDisplaySets(...entries) {
        this.removeAllData();
        for (const { displaySetId } of entries) {
            const waveform = await this.loadWaveformData(displaySetId);
            const durationMs = (waveform.numberOfSamples / waveform.samplingFrequency) * 1000;
            this.setDefaultDataPresentation(displaySetId, {
                visible: true,
                opacity: 1,
                visibleChannels: waveform.channels.map((_channel, index) => index),
                lineWidth: 1,
                amplitudeScale: 1,
                showGrid: true,
            });
            this.viewState = createDefaultECGViewState({
                timeRange: [0, durationMs],
                valueRange: getDefaultECGValueRange(waveform),
            });
            await this.addLoadedData(displaySetId, waveform, {
                renderMode: 'signal2d',
            });
        }
    }
    getWaveformData() {
        return this.getWaveformBindingData() ?? null;
    }
    getViewReference(_specifier = {}) {
        const dataId = this.getFirstBinding()?.data.id;
        return {
            FrameOfReferenceUID: this.getFrameOfReferenceUID(),
            dataId,
            referencedImageId: this.getCurrentImageId(),
            sliceIndex: 0,
        };
    }
    getViewReferenceId(_specifier = {}) {
        return `imageId:${this.getCurrentImageId()}`;
    }
    setViewReference(_viewRef) {
    }
    getSliceIndex() {
        return 0;
    }
    getZoom() {
        return (this.getResolvedView()?.zoom ?? Math.max(this.viewState.scale ?? 1, 0.001));
    }
    normalizeViewState(viewState) {
        return normalizeECGViewState(viewState);
    }
    getReferenceViewContexts() {
        const binding = this.getFirstBinding();
        if (!binding) {
            return super.getReferenceViewContexts();
        }
        return [
            {
                dataId: binding.data.id,
                dataIds: [binding.data.id],
                frameOfReferenceUID: this.getFrameOfReferenceUID(),
                imageIds: [binding.data.id],
                currentImageIdIndex: 0,
            },
        ];
    }
    setZoom(zoom, canvasPoint) {
        const resolvedView = this.getResolvedView();
        if (resolvedView) {
            this.applyResolvedViewState(resolvedView.withZoom(zoom, canvasPoint).state.viewState);
            return;
        }
        this.setViewState({
            scale: Math.max(zoom, 0.001),
            scaleMode: 'fit',
        });
    }
    getPan() {
        return this.getResolvedView()?.pan ?? [0, 0];
    }
    setPan(pan) {
        const resolvedView = this.getResolvedView();
        if (!resolvedView) {
            return;
        }
        this.applyResolvedViewState(resolvedView.withPan(pan).state.viewState);
    }
    getVisibleChannels() {
        const waveform = this.getWaveformBindingData();
        if (!waveform) {
            return [];
        }
        const dataId = waveform.id;
        const visibleChannels = new Set(this.getDisplaySetPresentation(dataId)?.visibleChannels ||
            waveform.channels.map((_channel, index) => index));
        return waveform.channels.map((channel, index) => ({
            name: channel.name,
            visible: visibleChannels.has(index),
        }));
    }
    getContentDimensions() {
        const rendering = this.getCurrentRendering();
        if (!rendering) {
            return { width: 0, height: 0 };
        }
        return {
            width: rendering.metrics.ecgWidth,
            height: rendering.metrics.ecgHeight,
        };
    }
    resize() {
        const { clientWidth, clientHeight } = this.canvas;
        if (this.canvas.width !== clientWidth ||
            this.canvas.height !== clientHeight) {
            const dpr = window.devicePixelRatio || 1;
            this.canvas.width = Math.floor(Math.max(1, clientWidth) * dpr);
            this.canvas.height = Math.floor(Math.max(1, clientHeight) * dpr);
        }
        this.render();
    }
    render() {
        this.renderBindings();
    }
    customRenderViewportToCanvas() {
        this.render();
    }
    resetViewState() {
        const previousCamera = this.getCameraForEvent();
        this.viewState = createDefaultECGViewState({
            timeRange: this.viewState.timeRange,
            valueRange: this.viewState.valueRange,
        });
        this.modified(previousCamera);
        this.triggerCameraResetEvent();
        return true;
    }
    getRotation() {
        return 0;
    }
    scroll() {
    }
    getCurrentImageId() {
        const binding = this.getFirstBinding();
        return binding?.data.id;
    }
    getCurrentImageIdIndex() {
        return 0;
    }
    getImageIds() {
        const binding = this.getFirstBinding();
        return binding ? [binding.data.id] : [];
    }
    getImageData() {
        const waveform = this.getWaveformBindingData();
        if (!waveform) {
            return null;
        }
        const nSamples = waveform.numberOfSamples;
        const nChannels = waveform.channels.length;
        const dimensions = [nSamples, ECG_AMPLITUDE_INDEX_SIZE, nChannels];
        const spacing = [1, 1, 1];
        const origin = [0, 0, 0];
        const direction = [1, 0, 0, 0, 1, 0, 0, 0, 1];
        const amplitudeOffset = ECG_AMPLITUDE_INDEX_SIZE / 2;
        const scalarData = new Int16Array(0);
        const imageData = {
            getDirection: () => direction,
            getDimensions: () => dimensions,
            getRange: () => [0, 1],
            getSpacing: () => spacing,
            worldToIndex: (point) => {
                return [point[0], point[1] + amplitudeOffset, point[2]];
            },
            indexToWorld: (point) => {
                return [point[0], point[1] - amplitudeOffset, point[2]];
            },
        };
        return {
            dimensions,
            spacing,
            origin,
            direction,
            imageData,
            scalarData,
            hasPixelSpacing: false,
            preScale: { scaled: false },
            metadata: { Modality: 'ECG', FrameOfReferenceUID: '' },
        };
    }
    setScaleAtCanvasPoint(scale, canvasPoint) {
        const resolvedView = this.getResolvedView();
        if (!resolvedView) {
            this.setZoom(scale);
            return;
        }
        this.applyResolvedViewState(resolvedView.withZoom(scale, canvasPoint).state.viewState);
    }
    async loadWaveformData(dataId) {
        const waveform = await this.dataProvider.load(dataId);
        if (!isECGWaveformData(waveform)) {
            throw new Error(`[ECGViewport] Loaded data for ${dataId} is not a valid ECG waveform`);
        }
        return waveform;
    }
    getWaveformBindingData() {
        const binding = this.getFirstBinding();
        if (!binding || !isECGWaveformData(binding.data)) {
            return;
        }
        return binding.data;
    }
    getCurrentRendering() {
        const binding = this.getFirstBinding();
        if (!binding || !isECGCanvasRendering(binding.rendering)) {
            return;
        }
        return binding.rendering;
    }
    getResolvedView() {
        const waveform = this.getWaveformBindingData();
        const rendering = this.getCurrentRendering();
        if (!waveform || !rendering) {
            return;
        }
        return new ECGResolvedView({
            viewState: this.viewState,
            canvas: this.canvas,
            dataPresentation: this.getDisplaySetPresentation(waveform.id),
            frameOfReferenceUID: `ecg-viewport-${this.id}`,
            metrics: rendering.metrics,
            waveform,
        });
    }
    applyResolvedViewState(nextViewState) {
        this.setViewState(nextViewState);
    }
}
export default ECGViewport;
function isECGWaveformData(data) {
    if (typeof data !== 'object' || data === null || data.type !== 'ecg') {
        return false;
    }
    const waveform = data;
    return (Array.isArray(waveform.channels) &&
        typeof waveform.numberOfSamples === 'number' &&
        typeof waveform.samplingFrequency === 'number' &&
        typeof waveform.numberOfChannels === 'number');
}
function isECGCanvasRendering(rendering) {
    return rendering.renderMode === 'signal2d';
}
